// @ts-nocheck
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  CacheLookupPolicy,
  InteractionStatus,
} from "@azure/msal-browser";
import * as Sentry from "@sentry/browser";
import { useCookies } from "react-cookie";

import { DATA_URLS } from "@/urls";
import {
  internalAPI,
  getErrorsFromResponse,
  onLoadingError,
  ApiErrors,
} from "@/utils/api";
import { parseJWT } from "@/utils/functions";

export interface AuthUser {
  id?: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  fullName?: string;
}

export interface LoginOwner {
  username?: string;
  fullName?: string;
}

export interface MembershipItem {
  groupCodename?: string;
  permissions?: string[];
  contactName?: string;
  companyID?: number;
  companyName?: string;
  isSelected?: boolean;
}

export interface SelectedMembership {
  companyName?: string;
  groupCodename?: string;
  permissions?: string[];
  contactName?: string;
}

export interface CompanyInfo {
  id?: number | null;
  name?: string | null;
  type?: string | null;
  logo?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  enableSupportSection?: boolean;
  enableAPIAccess?: boolean;
  enableDDoSProtectIntegration?: boolean;
  enableVulnerabilityReports?: boolean;
}

interface CallbackConfig {
  onSuccess: () => void;
  onError?: (data: { errors: ApiErrors | undefined }) => void;
}

export interface AuthContextValue {
  accessToken: string | undefined;
  idToken: string | undefined;
  idTokenValidating: boolean;
  idTokenValidationError: any;
  authToken: string | undefined;
  user: AuthUser;
  loginOwner: LoginOwner;
  membership: MembershipItem[] | undefined;
  selectedMembership: SelectedMembership;
  company: CompanyInfo;
  loginAs: (user: { id: number }, config: CallbackConfig) => void;
  reLoginAsSelf: (config: CallbackConfig) => void;
  // Resolves only if the sign-in redirect could not start (the page
  // navigates away otherwise), so callers can re-enable their button.
  login: (loginParams?: Record<string, any>) => Promise<void>;
  logout: () => void;
  getCurrentUser: () => void;
  changePassword: (
    data: { new_password: string; current_password: string },
    config: CallbackConfig,
  ) => void;
  updateProfile: (data: Record<string, any>, config: CallbackConfig) => void;
  getRedirectUri: () => string | null;
  logActivity: () => void;
  sendAccessNotification: () => Promise<any>;
  switchCompany: (companyId: number) => Promise<any>;
  getMSALRequestParams: () => any;
}

export const AuthContext = React.createContext<AuthContextValue>(
  {} as AuthContextValue,
);

const SIGN_IN_SCOPES = ["User.Read"];

// A sign-in redirect that never starts or never leaves the page (MSAL gives up
// after a timeout, e.g. in a throttled background tab), or one refused because
// another redirect is already under way, is not an app error. Anything else is.
const reportSignInRedirectFailure = (error: unknown) => {
  console.warn("Microsoft sign-in redirect failed", error);
  const expected =
    error instanceof BrowserAuthError &&
    (error.errorCode === BrowserAuthErrorCodes.timedOut ||
      error.errorCode === BrowserAuthErrorCodes.interactionInProgress);
  if (!expected) {
    Sentry.captureException(error);
  }
};

interface AuthContextProviderProps {
  children: ReactNode;
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [cookies] = useCookies();
  const {
    instance: msalInstance,
    accounts: msalAccounts,
    inProgress,
  } = useMsal();
  const storedUser = localStorage.getItem("user");
  const [authToken, setAuthToken] = useState<string | undefined>(
    storedUser ? JSON.parse(storedUser).token : undefined,
  );
  const [user, setUser] = useState<AuthUser>({
    id: undefined,
    username: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    fullName: undefined,
  });
  const [loginOwner, setLoginOwner] = useState<LoginOwner>({
    username: undefined,
    fullName: undefined,
  });
  const [selectedMembership, setSelectedMembership] =
    useState<SelectedMembership>({
      groupCodename: undefined,
      contactName: undefined,
    });
  const [membership, setMembership] = useState<MembershipItem[] | undefined>();
  const [company, setCompany] = useState<CompanyInfo>({
    name: undefined,
    type: undefined,
    logo: undefined,
    primaryColor: undefined,
    secondaryColor: undefined,
    enableSupportSection: undefined,
    enableAPIAccess: undefined,
    enableDDoSProtectIntegration: undefined,
    enableVulnerabilityReports: undefined,
  });
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [idToken, setIDToken] = useState<string | undefined>(undefined);
  const [idTokenValidating, setIDTokenValidating] = useState(true);
  const [idTokenValidationError, setIDTokenValidationError] =
    useState<any>(undefined);

  // Interactive fallback for when silent token acquisition fails (e.g. the
  // refresh token has expired). The redirect navigates away; if it doesn't,
  // show an error instead of leaving the user on the loading spinner.
  const redirectToSignIn = useCallback(
    (account: AccountInfo): Promise<void> =>
      msalInstance
        .acquireTokenRedirect({ scopes: SIGN_IN_SCOPES, account })
        .catch((error) => {
          reportSignInRedirectFailure(error);
          // interaction_in_progress gets the error card too: nothing else
          // would ever clear the spinner, and MSAL drops its interaction flag
          // when it refuses the call, so the suggested reload can sign in.
          setIDTokenValidating(false);
          setIDTokenValidationError(
            "Unable to redirect to Microsoft sign-in. Reload the page to try again.",
          );
        }),
    [msalInstance],
  );

  const validateIDToken = useCallback(
    (idTokenValue: string, allowRefreshRetry = true): Promise<void> => {
      setIDTokenValidating(true);
      return internalAPI
        .post(DATA_URLS.internal.auth.validateADToken.buildPath(), {
          token: idTokenValue,
        })
        .then((response) => {
          const userData = {
            token: response.data.token,
            username: response.data.username,
          };
          localStorage.setItem("user", JSON.stringify(userData));
          setUser((value) => {
            return {
              ...value,
              username: userData.username,
            };
          });
          setAuthToken(userData.token);
          setIDTokenValidating(false);
        })
        .catch((error) => {
          // MSAL silently returns a cached id token, which can already be
          // expired (~1h lifetime) even within an otherwise-valid session. The
          // backend flags this with code "ad_token_expired" (HTTP 400, not 401,
          // so the global reauth interceptor isn't triggered). Force one fresh
          // acquisition and retry once before surfacing an error to the user.
          const tokenExpired =
            error?.response?.data?.code === "ad_token_expired";
          const account = msalInstance.getAllAccounts()[0];
          if (tokenExpired && allowRefreshRetry && account) {
            // If no fresh token is available without user interaction
            // (typically an expired refresh token), sign in again rather than
            // show an authorization error.
            return msalInstance
              .acquireTokenSilent({
                scopes: SIGN_IN_SCOPES,
                account,
                forceRefresh: true,
              })
              .then((fresh) => {
                setIDToken(fresh.idToken);
                return validateIDToken(fresh.idToken, false);
              })
              .catch(() => redirectToSignIn(account));
          }
          const errors = getErrorsFromResponse(error);
          setIDTokenValidating(false);
          setIDTokenValidationError(errors);
        });
    },
    [msalInstance, redirectToSignIn],
  );

  const loginAs = (targetUser: { id: number }, config: CallbackConfig) => {
    internalAPI
      .post(DATA_URLS.internal.auth.loginAs.buildPath(), {
        user: targetUser.id,
      })
      .then(() => {
        config.onSuccess();
      })
      .catch(onLoadingError);
  };

  const reLoginAsSelf = (config: CallbackConfig) => {
    internalAPI
      .post(DATA_URLS.internal.auth.reLoginAsSelf.buildPath())
      .then(() => {
        config.onSuccess();
      })
      .catch(onLoadingError);
  };

  const login = (loginParams: Record<string, any> = {}): Promise<void> => {
    if (inProgress !== InteractionStatus.None) {
      return Promise.resolve();
    }
    return msalInstance
      .loginRedirect(loginParams)
      .catch(reportSignInRedirectFailure);
  };

  const logout = () => {
    if (loginOwner.username) {
      reLoginAsSelf({
        onSuccess: () => {
          localStorage.removeItem("user");
          msalInstance.logoutRedirect();
        },
      });
    } else {
      localStorage.removeItem("user");
      msalInstance.logoutRedirect();
    }
  };

  const getCurrentUser = useCallback(() => {
    internalAPI
      .get(DATA_URLS.internal.auth.userInfo.buildPath())
      .then((response) => {
        const {
          user: userData,
          membership: membershipData,
          company: companyData,
          login_owner,
        } = response.data;
        const selectedMembershipData = membershipData.filter(
          (m: any) => m.is_selected,
        )[0];
        setUser({
          id: userData.id,
          username: userData.username,
          firstName: userData.first_name,
          lastName: userData.last_name,
          email: userData.email,
          fullName: userData.full_name,
        });
        setMembership(
          membershipData.map((m: any) => {
            return {
              groupCodename: m.group?.codename,
              permissions: m.group?.permissions.map((p: any) => p.codename),
              contactName: m.contact?.name,
              companyID: m.company?.id,
              companyName: m.company?.name,
              isSelected: m.is_selected,
            };
          }),
        );
        setSelectedMembership({
          companyName: selectedMembershipData.company?.name,
          groupCodename: selectedMembershipData.group?.codename,
          permissions: selectedMembershipData.group?.permissions.map(
            (p: any) => p.codename,
          ),
          contactName: selectedMembershipData.contact?.name,
        });
        setCompany({
          id: companyData ? companyData.id : null,
          name: companyData ? companyData.name : null,
          type: companyData ? companyData.type : null,
          primaryColor: companyData ? companyData.primary_color : null,
          secondaryColor: companyData ? companyData.secondary_color : null,
          logo: companyData ? companyData.logo : null,
          enableSupportSection: companyData
            ? companyData.enable_connectwise_support
            : false,
          enableAPIAccess: companyData ? companyData.enable_api_access : false,
          enableDDoSProtectIntegration: companyData
            ? companyData.enable_ddos_protect_integration
            : false,
          enableVulnerabilityReports: companyData
            ? companyData.enable_vulnerability_reports
            : false,
        });
        setLoginOwner(
          login_owner
            ? {
                username: login_owner.username,
                fullName: login_owner.full_name,
              }
            : {
                username: undefined,
                fullName: undefined,
              },
        );
      })
      .catch(onLoadingError);
  }, []);

  const changePassword = (
    data: { new_password: string; current_password: string },
    config: CallbackConfig,
  ) => {
    internalAPI
      .post(DATA_URLS.internal.auth.changePassword.buildPath(), {
        new_password: data.new_password,
        current_password: data.current_password,
      })
      .then(() => {
        config.onSuccess();
      })
      .catch((error) => {
        config.onError?.({ errors: getErrorsFromResponse(error) });
      });
  };

  const updateProfile = (data: Record<string, any>, config: CallbackConfig) => {
    internalAPI
      .patch(DATA_URLS.internal.auth.userInfo.buildPath(), data)
      .then(() => {
        config.onSuccess();
      })
      .catch((error) => {
        config.onError?.({ errors: getErrorsFromResponse(error) });
      });
  };

  const getRedirectUri = (): string | null => {
    return localStorage.getItem("redirectUri");
  };

  const logActivity = () => {
    // silenceReauth: this is fire-and-forget telemetry on a 5s interval; a
    // 401 here must not trigger the global reauth reload, or a single auth
    // failure becomes a reload loop on the polling cadence.
    internalAPI
      .post(
        DATA_URLS.internal.auth.logActivity.buildPath(),
        {},
        {
          silenceReauth: true,
        },
      )
      .catch((error) => {
        console.error(error);
      });
  };

  const sendAccessNotification = () => {
    return internalAPI.post(
      DATA_URLS.internal.auth.accessNotification.buildPath(),
      {},
    );
  };

  const switchCompany = (companyId: number) => {
    return internalAPI.post(DATA_URLS.internal.auth.switchCompany.buildPath(), {
      company: companyId,
    });
  };

  const getMSALRequestParams = () => {
    const clientId = msalInstance.config.auth.clientId;
    const requestStateCookie = cookies[`msal.${clientId}.request.params`];
    return parseJWT(requestStateCookie);
  };

  useEffect(() => {
    if (msalAccounts.length > 0 && inProgress === InteractionStatus.None) {
      const request = {
        scopes: SIGN_IN_SCOPES,
        account: msalAccounts[0],
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      };

      msalInstance
        .acquireTokenSilent(request)
        .then((response) => {
          setAccessToken(response.accessToken);
          setIDToken(response.idToken);
          validateIDToken(response.idToken).then(() => {
            getCurrentUser();
          });
        })
        .catch(() => {
          // Cache/refresh token failed, fall back to interactive redirect
          if (inProgress === InteractionStatus.None) {
            redirectToSignIn(msalAccounts[0]);
          }
        });
    }
  }, [
    getCurrentUser,
    validateIDToken,
    redirectToSignIn,
    msalInstance,
    msalAccounts,
    inProgress,
  ]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        idToken,
        idTokenValidating,
        idTokenValidationError,
        authToken,
        user,
        loginOwner,
        membership,
        selectedMembership,
        company,
        loginAs,
        reLoginAsSelf,
        login,
        logout,
        getCurrentUser,
        changePassword,
        updateProfile,
        getRedirectUri,
        logActivity,
        sendAccessNotification,
        switchCompany,
        getMSALRequestParams,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
